/**
 * Document Picker from Encompass eFolder
 *
 * A reusable component for selecting documents from Encompass eFolder
 * Used in the title/escrow assignment flow to attach documents to the portal
 *
 * Now receives pre-fetched documents as props for better performance
 * (documents are loaded when modal opens, not when user reaches this step)
 */

import { useState, useMemo, useCallback } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  Search,
  Loader2,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  Eye
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { EncompassDocumentWithAttachments, EncompassAttachment } from '@/types/encompass-documents';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('DocumentPickerFromEfolder', 'conditions-portal');

interface DocumentPickerFromEfolderProps {
  // Pre-fetched document data (from parent)
  documents: EncompassDocumentWithAttachments[];
  loading: boolean;
  error: string | null;
  // Selection state
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  // Preview callback - called when user clicks document row (not checkbox)
  onPreviewAttachment?: (attachment: EncompassAttachment | null) => void;
  // Currently previewing attachment (for visual highlight)
  previewingId?: string | null;
  maxHeight?: string;
}

interface FolderNodeProps {
  document: EncompassDocumentWithAttachments;
  selectedIds: Set<string>;
  previewingId?: string | null;
  onToggle: (attachmentId: string) => void;
  onPreview: (attachment: EncompassAttachment) => void;
  searchQuery: string;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) {
return '';
}
  if (bytes < 1024) {
return `${bytes} B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(0)} KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format folder title - remove common prefixes
 */
function formatFolderTitle(title: string | null | undefined): string {
  if (!title) {
return 'Untitled';
}
  return title.replace(/^\d+\s*-\s*/, '').trim() || 'Untitled';
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
return '';
}
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Single folder with expandable attachments
 */
function FolderNode({ document, selectedIds, previewingId, onToggle, onPreview, searchQuery }: FolderNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Only show downloaded, active attachments
  const availableAttachments = useMemo(() => {
    return document.attachments.filter(att =>
      att.download_status === 'downloaded' && att.is_active !== false
    );
  }, [document.attachments]);

  // Filter by search query
  const filteredAttachments = useMemo(() => {
    if (!searchQuery) {
return availableAttachments;
}
    const query = searchQuery.toLowerCase();
    return availableAttachments.filter(att =>
      att.title?.toLowerCase().includes(query)
    );
  }, [availableAttachments, searchQuery]);

  // Skip folder if no matching attachments
  if (filteredAttachments.length === 0) {
return null;
}

  // Selection state for folder
  const selectedCount = filteredAttachments.filter(a => selectedIds.has(a.id)).length;
  const allSelected = selectedCount === filteredAttachments.length;
  const someSelected = selectedCount > 0 && selectedCount < filteredAttachments.length;

  const handleFolderToggle = () => {
    if (allSelected) {
      // Deselect all
      filteredAttachments.forEach(att => {
        if (selectedIds.has(att.id)) {
          onToggle(att.id);
        }
      });
    } else {
      // Select all
      filteredAttachments.forEach(att => {
        if (!selectedIds.has(att.id)) {
          onToggle(att.id);
        }
      });
    }
  };

  return (
    <div className="mb-2">
      {/* Folder header */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 cursor-pointer rounded-lg transition-all',
          'bg-muted/50 hover:bg-muted',
          isExpanded && 'bg-muted'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="text-muted-foreground flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>

        {/* Folder checkbox */}
        <Checkbox
          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
          onClick={(e) => {
            e.stopPropagation();
            handleFolderToggle();
          }}
          className="mr-1"
        />

        {/* Folder icon */}
        {isExpanded ? (
          <FolderOpen className="h-4 w-4 text-amber-500 flex-shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
        )}

        {/* Folder name */}
        <span className="flex-1 text-sm font-medium truncate">
          {formatFolderTitle(document.title)}
        </span>

        {/* Count badge */}
        <Badge variant="secondary" className="h-5 px-1.5 text-xs">
          {selectedCount > 0 && `${selectedCount}/`}{filteredAttachments.length}
        </Badge>
      </div>

      {/* Attachments list */}
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-0.5">
          {filteredAttachments.map(attachment => {
            const isSelected = selectedIds.has(attachment.id);
            const isPreviewing = previewingId === attachment.id;

            return (
              <div
                key={attachment.id}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors',
                  'hover:bg-muted/50',
                  isSelected && 'bg-primary/10',
                  isPreviewing && 'ring-2 ring-primary/50 bg-primary/5'
                )}
                onClick={() => onPreview(attachment)}
              >
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={() => onToggle(attachment.id)}
                />

                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />

                <span className="flex-1 text-sm truncate">
                  {attachment.title || 'Untitled'}
                </span>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {isPreviewing && (
                    <Eye className="h-3 w-3 text-primary" />
                  )}
                  {attachment.uploaded_date && (
                    <span>{formatDate(attachment.uploaded_date)}</span>
                  )}
                  {attachment.file_size && (
                    <span>{formatFileSize(attachment.file_size)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Document picker component
 */
export function DocumentPickerFromEfolder({
  documents,
  loading,
  error,
  selectedIds,
  onSelectionChange,
  onPreviewAttachment,
  previewingId,
  maxHeight = '400px'
}: DocumentPickerFromEfolderProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Get all available attachments (downloaded and active)
  const allAvailableAttachments = useMemo(() => {
    const attachments: EncompassAttachment[] = [];
    for (const doc of documents) {
      for (const att of doc.attachments) {
        if (att.download_status === 'downloaded' && att.is_active !== false) {
          attachments.push(att);
        }
      }
    }
    return attachments;
  }, [documents]);

  // Toggle selection handler (checkbox only)
  const handleToggle = useCallback((attachmentId: string) => {
    const newIds = new Set(selectedIds);
    if (newIds.has(attachmentId)) {
      newIds.delete(attachmentId);
    } else {
      newIds.add(attachmentId);
    }
    onSelectionChange(newIds);
    log.info('Document selection changed', { attachmentId, selected: newIds.has(attachmentId) });
  }, [selectedIds, onSelectionChange]);

  // Preview handler (row click)
  const handlePreview = useCallback((attachment: EncompassAttachment) => {
    if (onPreviewAttachment) {
      onPreviewAttachment(attachment);
    }
  }, [onPreviewAttachment]);

  // Select/deselect all
  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === allAvailableAttachments.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(allAvailableAttachments.map(a => a.id)));
    }
  }, [selectedIds.size, allAvailableAttachments, onSelectionChange]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-sm text-red-600 mb-3">{error}</p>
      </div>
    );
  }

  // No documents state
  if (allAvailableAttachments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground mb-1">No documents available</p>
        <p className="text-xs text-muted-foreground">
          Documents must be downloaded from Encompass first
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 h-full flex flex-col">
      {/* Search and controls */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSelectAll}
          className="whitespace-nowrap"
        >
          {selectedIds.size === allAvailableAttachments.length ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      {/* Selection summary */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">
            {selectedIds.size} document{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
        </div>
      )}

      {/* Document tree */}
      <ScrollArea className="border rounded-md flex-1" style={{ height: maxHeight }}>
        <div className="space-y-1 p-3">
          {documents.map(doc => (
            <FolderNode
              key={doc.id}
              document={doc}
              selectedIds={selectedIds}
              previewingId={previewingId}
              onToggle={handleToggle}
              onPreview={handlePreview}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

/**
 * Get selected attachments as objects (for passing to parent)
 * Now accepts documents as parameter instead of fetching
 */
export function getSelectedAttachments(
  documents: EncompassDocumentWithAttachments[],
  selectedIds: Set<string>
): EncompassAttachment[] {
  const result: EncompassAttachment[] = [];
  for (const doc of documents) {
    for (const att of doc.attachments) {
      if (selectedIds.has(att.id)) {
        result.push(att);
      }
    }
  }
  return result;
}

export default DocumentPickerFromEfolder;
